import { useCallback, useMemo, useState } from 'react';
import { Box } from '@mui/material';
import { TableTitleBox } from '../../common/components';
import MovementDialog from '../bank-movements/components/MovementDialog';
import { useBankAccounts } from '../../hooks/useBankAccounts';
import { useMovementTypes } from '../../hooks/useMovementTypes';
import { useExchangeChecks } from '../../hooks/useBankMovements';
import ExchangeChecksFilters from './components/ExchangeChecksFilters';
import ExchangeChecksTable from './components/ExchangeChecksTable';
import {
  EXCHANGE_CHECKS_DEFAULT_FILTERS,
  EXCHANGE_CHECKS_ITEMS_PER_PAGE,
} from './constants/exchangeChecksConstants';
import { exportExchangeChecksToExcel } from './utils/exportExcel';
import { formatBankName } from '../bank-movements/utils/formatters';

const ExchangeChecks = () => {
  const { data: bankAccounts = [] } = useBankAccounts();
  const { data: movementTypes = [] } = useMovementTypes();

  const allowedMovementTypes = useMemo(
    () => movementTypes.filter((type) => type.is_active !== false),
    [movementTypes]
  );

  const [filters, setFilters] = useState(EXCHANGE_CHECKS_DEFAULT_FILTERS);
  const [searchTerm, setSearchTerm] = useState('');
  const [reconciliationFilter, setReconciliationFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(EXCHANGE_CHECKS_ITEMS_PER_PAGE);
  const [movementDialogOpen, setMovementDialogOpen] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState(null);

  const queryFilters = useMemo(
    () => ({
      start_date: filters.startDate,
      end_date: filters.endDate,
      account_id: filters.accountId || undefined,
    }),
    [filters.startDate, filters.endDate, filters.accountId]
  );

  const { data: exchangeChecks = [], isLoading, refetch } = useExchangeChecks(queryFilters);

  const accountMap = useMemo(() => {
    const map = new Map();
    bankAccounts.forEach((account) => {
      map.set(account.uuid, `${formatBankName(account.bank_name)} - ${account.account_number}`);
    });
    return map;
  }, [bankAccounts]);

  const enhancedChecks = useMemo(
    () =>
      (exchangeChecks || []).map((movement) => ({
        ...movement,
        accountLabel: accountMap.get(movement.uuid_bank_account) || 'Sin cuenta',
      })),
    [exchangeChecks, accountMap]
  );

  const filteredData = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return enhancedChecks.filter((movement) => {
      const matchesAccount =
        !filters.accountId || movement.uuid_bank_account === filters.accountId;

      const matchesSearch =
        !normalizedSearch ||
        movement.concept?.toLowerCase().includes(normalizedSearch) ||
        movement.voucher_number?.toLowerCase().includes(normalizedSearch) ||
        movement.legal_entity_name?.toLowerCase().includes(normalizedSearch) ||
        movement.accountLabel?.toLowerCase().includes(normalizedSearch);

      const matchesReconciliation =
        reconciliationFilter === 'all' ||
        (reconciliationFilter === 'reconciled' && movement.is_reconciled) ||
        (reconciliationFilter === 'pending' && !movement.is_reconciled);

      const matchesType = typeFilter === 'all' || movement.uuid_movement_type === typeFilter;

      return matchesAccount && matchesSearch && matchesReconciliation && matchesType;
    });
  }, [enhancedChecks, filters.accountId, reconciliationFilter, searchTerm, typeFilter]);

  const totalPages = Math.ceil(filteredData.length / rowsPerPage) || 1;
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleExport = async () => {
    await exportExchangeChecksToExcel(filteredData);
  };

  const handleEditMovement = useCallback(
    (movement) => {
      const account = bankAccounts.find((acc) => acc.uuid === movement.uuid_bank_account);
      if (!account) {
        console.warn('No se encontró la cuenta asociada al movimiento');
        return;
      }
      setSelectedAccount(account);
      setSelectedMovement(movement);
      setMovementDialogOpen(true);
    },
    [bankAccounts]
  );

  const handleCloseDialog = useCallback(() => {
    setMovementDialogOpen(false);
    setSelectedMovement(null);
    refetch();
  }, [refetch]);

  return (
    <Box>
      <TableTitleBox title="Bancos | Cheques de canje" />

      <ExchangeChecksFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        bankAccounts={bankAccounts}
        searchTerm={searchTerm}
        onSearchChange={(value) => {
          setSearchTerm(value);
          setPage(1);
        }}
        reconciliationFilter={reconciliationFilter}
        onReconciliationChange={(value) => {
          setReconciliationFilter(value);
          setPage(1);
        }}
        typeFilter={typeFilter}
        onTypeChange={(value) => {
          setTypeFilter(value);
          setPage(1);
        }}
        availableMovementTypes={allowedMovementTypes}
        onExport={handleExport}
      />

      <ExchangeChecksTable
        movements={paginatedData}
        totalItems={filteredData.length}
        page={safePage}
        totalPages={totalPages}
        rowsPerPage={rowsPerPage}
        onPageChange={(event, value) => setPage(value)}
        onRowsPerPageChange={handleRowsPerPageChange}
        isLoading={isLoading}
        onEditMovement={handleEditMovement}
        onRefresh={refetch}
      />

      <MovementDialog
        open={movementDialogOpen}
        onClose={handleCloseDialog}
        movement={selectedMovement}
        account={selectedAccount}
        editMode
      />
    </Box>
  );
};

export default ExchangeChecks;

